// frontend/admin-portal/src/components/sales/InvoiceItemsEditor.jsx
import React from "react";
import { Button, Input, formatMoney } from "@fiberhome/shared-ui";
import ProductPicker from "./ProductPicker";

/**
 * Editor for invoice line items.
 * Grid layout — no Bootstrap row misalignment.
 * Each row: [#] [Product search / name] [Qty] [Unit] [Price] [Disc] [Tax%] [Line Total] [✕]
 */
export default function InvoiceItemsEditor({ items, onChange, currency }) {
  const setItem = (idx, key, value) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };

  const addProduct = (p) => {
    onChange([
      ...items,
      {
        productId: p.id,
        description: p.name,
        productSku: p.sku,
        quantity: 1,
        unit: p.unit || "pcs",
        unitPrice: Number(p.price) || 0,
        discount: 0,
        taxRate: Number(p.taxRate) || 0,
        unitCostSnapshot: Number(p.totalCost) || Number(p.costPrice) || 0,
      },
    ]);
  };

  const addCustomLine = () => {
    onChange([
      ...items,
      {
        productId: null,
        description: "",
        productSku: "",
        quantity: 1,
        unit: "pcs",
        unitPrice: 0,
        discount: 0,
        taxRate: 0,
        unitCostSnapshot: 0,
      },
    ]);
  };

  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));

  const lineTotal = (it) => {
    const gross =
      (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0) -
      (Number(it.discount) || 0);
    const tax = (gross * (Number(it.taxRate) || 0)) / 100;
    return gross + tax;
  };

  return (
    <div className="fh-invoice-items">
      <div className="fh-invoice-items__picker">
        <ProductPicker onPick={addProduct} />
        <Button
          variant="outline"
          size="sm"
          onClick={addCustomLine}
          type="button"
        >
          + Custom line
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="fh-invoice-items__empty">
          Search for a product above to add the first item.
        </div>
      ) : (
        <div className="fh-invoice-items__grid">
          {/* header */}
          <div className="fh-invoice-items__header">
            <span>#</span>
            <span>Product / Description</span>
            <span>Qty</span>
            <span>Unit</span>
            <span>Price</span>
            <span>Disc</span>
            <span>Tax %</span>
            <span>Total</span>
            <span />
          </div>

          {/* rows */}
          {items.map((it, idx) => (
            <div className="fh-invoice-items__row" key={idx}>
              <div className="fh-invoice-items__idx">{idx + 1}</div>

              <div className="fh-invoice-items__cell">
                {it.productId ? (
                  <div className="fh-invoice-items__product">
                    <div className="fh-invoice-items__product-name">
                      {it.description}
                    </div>
                    <div className="fh-invoice-items__product-sku">
                      {it.productSku || "—"}
                    </div>
                  </div>
                ) : (
                  <Input
                    value={it.description}
                    placeholder="Description"
                    onChange={(e) =>
                      setItem(idx, "description", e.target.value)
                    }
                  />
                )}
              </div>

              <div className="fh-invoice-items__cell">
                <input
                  type="number"
                  className="fh-input"
                  min={0}
                  value={it.quantity}
                  onChange={(e) => setItem(idx, "quantity", e.target.value)}
                />
              </div>

              <div className="fh-invoice-items__cell">
                <input
                  type="text"
                  className="fh-input"
                  value={it.unit}
                  onChange={(e) => setItem(idx, "unit", e.target.value)}
                />
              </div>

              <div className="fh-invoice-items__cell">
                <input
                  type="number"
                  className="fh-input"
                  min={0}
                  step="0.01"
                  value={it.unitPrice}
                  onChange={(e) => setItem(idx, "unitPrice", e.target.value)}
                />
              </div>

              <div className="fh-invoice-items__cell">
                <input
                  type="number"
                  className="fh-input"
                  min={0}
                  step="0.01"
                  value={it.discount}
                  onChange={(e) => setItem(idx, "discount", e.target.value)}
                />
              </div>

              <div className="fh-invoice-items__cell">
                <input
                  type="number"
                  className="fh-input"
                  min={0}
                  step="0.01"
                  value={it.taxRate}
                  onChange={(e) => setItem(idx, "taxRate", e.target.value)}
                />
              </div>

              <div className="fh-invoice-items__total">
                {formatMoney(lineTotal(it), { symbol: currency })}
              </div>

              <div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRow(idx)}
                  title="Remove"
                >
                  ✕
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
